import _ from 'underscore';
import { buildRange } from './RangeExtractorHelpers';

// Allowed HTTP methods
export const HTTP_METHODS = {
    GET: 'GET',
    POST: 'POST',
    PUT: 'PUT',
    DELETE: 'DELETE',
};

// Clean the parameters
function cleanParams(params, omitedParams = []) {
    return _.omit(params, omitedParams);
}

/**
 * Extract the required parameters from an url
 * ie. /orgas/:orgaId/event/:id
 * => ['orgaId', 'id']
 * @param {string} url
 */
function extractRequiredParameters(url) {
    const requiredParameters = [];
    const regex = /:(\w+)/g;
    let matches = null;
    /* eslint-disable-next-line no-cond-assign */
    while ((matches = regex.exec(url)) !== null) {
        requiredParameters.push(matches[1]);
    }
    return requiredParameters;
}

/**
 * Replace the parameters pattern by the parameters from an url
 * @param {string} originalUrl
 * @param {array} requiredParameters
 * @param {object} params
 */
function replaceParameters(originalUrl, requiredParameters, params) {
    return _.reduce(requiredParameters, (url, requiredParameter) => url.replace(`:${requiredParameter}`, params[requiredParameter]), originalUrl);
}

/**
 * Create an action creator
 * @param {object} params
 * @param {string} params.type The redux action type
 * @param {string} params.method An allowed HTTP_METHODS
 * @param {string} params.url The url with parameter ie. /events/:id
 * @param {bool} params.withPagination Enable the pagination system
 * @param {bool} params.responseType Response type
 */
export default function createActionCreator(params) {
    const requiredParameters = extractRequiredParameters(params.url);
    return function genericActionCreator(parameters = {}, pagination = {}, extras = {}) {
        const isFormData = (parameters instanceof FormData);
        let cleanedParams = parameters;
        if (!isFormData) {
            cleanedParams = cleanParams(parameters, requiredParameters);
        }
        // Build the range parameter if pagination enable
        if (params.withPagination) {
            cleanedParams.range = buildRange(
                pagination.page,
                pagination.pageSize,
            );
        }
        const headers = {};
        if (params.formData) {
            headers['Content-Type'] = 'multipart/form-data';
        }
        let parametersKey = 'params';
        if ([HTTP_METHODS.POST, HTTP_METHODS.PUT].indexOf(params.method) > -1) {
            parametersKey = 'data';
        }
        return {
            type: params.type,
            payload: {
                request: {
                    method: params.method,
                    url: replaceParameters(params.url, requiredParameters, parameters),
                    [parametersKey]: cleanedParams,
                    responseType: params.responseType,
                    headers,
                },
                extras: {
                    actionType: params.type,
                    identifier: Math.random().toString(32).slice(2),
                    parameters,
                    pagination: params.withPagination ? pagination : undefined,
                    ...extras,
                },
            },
        };
    };
}

/**
 * Create a dynamic action creator from parameters
 * @param {string} paramKey The parameters key (ie. orgaId)
 * @param {string} actionName The action name (ie. SET_ORGA_ID)
 */
export function rawActionCreatorFactory(paramKey, actionName) {
    return (paramValue) => ({
        type: actionName,
        payload: {
            [paramKey]: paramValue,
        },
    });
}
